import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useTrace } from '../context/TraceContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  Link2, CheckCircle2, AlertTriangle, RefreshCw,
  Database, Server, FlaskConical, Wrench
} from 'lucide-react';

const IntegrationsPage = () => {
  const { pidTraceMode } = useTrace();

  const integrations = [
    { id: 'ERP', name: 'SAP ERP', type: 'ERP', status: 'Connected', lastSync: '2 min ago', icon: Database },
    { id: 'MES', name: 'Manufacturing Execution System', type: 'MES', status: 'Connected', lastSync: '5 min ago', icon: Server },
    { id: 'LIMS', name: 'Laboratory Information System', type: 'LIMS', status: 'Connected', lastSync: '10 min ago', icon: FlaskConical },
    { id: 'CMMS', name: 'Computerized Maintenance System', type: 'CMMS', status: 'Warning', lastSync: '2 hours ago', icon: Wrench }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Connected': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Warning': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Disconnected': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Integrations
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            ERP, MES, LIMS, and CMMS integration status and sync monitoring
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* <ReportExportButton 
            title="Integrations Report"
            subtitle="ERP, MES, LIMS, and CMMS integration status and sync monitoring"
          /> */}
          <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync All
          </Button>
        </div>
      </div>

      <ReportSection id="integrations-metrics" title="Integrations Metrics" description="Integration status overview">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §8" req="SM-CSV-001" />
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Total Integrations</p>
              <p className="text-2xl font-bold text-slate-900">{integrations.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Connected</p>
              <p className="text-2xl font-bold text-emerald-600">
                {integrations.filter(i => i.status === 'Connected').length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Warnings</p>
              <p className="text-2xl font-bold text-amber-600">
                {integrations.filter(i => i.status === 'Warning').length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Data Integrity</p>
              <p className="text-2xl font-bold text-emerald-600">100%</p>
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      <ReportSection id="integrations-list" title="Integrations List" description="All system integrations">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {integrations.map(integration => {
            const Icon = integration.icon;
            return (
              <Card key={integration.id} className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
                <TraceLabel prd="PRD §8" req="SM-DI-001" />
                <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-md">
                      <Icon className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-800">{integration.name}</h3>
                      <p className="text-xs text-slate-500">{integration.type}</p>
                    </div>
                  </div>
                  <Badge className={`${getStatusColor(integration.status)} text-xs`}>
                    {integration.status === 'Connected' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                    {integration.status === 'Warning' && <AlertTriangle className="h-3 w-3 mr-1" />}
                    {integration.status}
                  </Badge>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Last sync: {integration.lastSync}</span>
                  <Button size="sm" variant="ghost" className="text-slate-600 hover:text-slate-800">
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Sync
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      </ReportSection>

      <Card className="bg-blue-50 border border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Link2 className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Integration Architecture</p>
              <p className="text-xs text-blue-700 mt-1">
                All integrations use secure API connections with full audit trail logging. 
                Data synchronization follows ALCOA+ principles for data integrity compliance.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsPage;
